/** Run `cb` once, after the window `load` event, during idle time — the slot
 *  for work that must never contend with the LCP window (analytics init, nav
 *  prewarms). Safari has no requestIdleCallback, so a post-load timeout keeps
 *  the work off the critical path there; the 5s idle timeout bounds how long
 *  a busy page can starve the callback. Returns a cancel function that fully
 *  disarms the schedule (load listener + idle/timeout handle). */
export function scheduleAfterLoadIdle(
  cb: () => void,
  { fallbackDelayMs = 1500 }: { fallbackDelayMs?: number } = {},
): () => void {
  if (typeof window === "undefined") return () => {};
  const hasRic = typeof window.requestIdleCallback === "function";
  let handle: number | undefined;
  let cancelled = false;
  const run = (): void => {
    if (!cancelled) cb();
  };
  const schedule = (): void => {
    handle = hasRic
      ? window.requestIdleCallback(run, { timeout: 5000 })
      : window.setTimeout(run, fallbackDelayMs);
  };
  if (document.readyState === "complete") schedule();
  else window.addEventListener("load", schedule, { once: true });
  return () => {
    cancelled = true;
    window.removeEventListener("load", schedule);
    if (handle === undefined) return;
    if (hasRic) window.cancelIdleCallback(handle);
    else window.clearTimeout(handle);
  };
}
